import React from "react";
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Calendar, Target, RefreshCw } from "lucide-react";

interface ActionPlanTask {
  id: string;
  day_number: number;
  task: string;
  status: string;
}

interface ActionPlanSectionProps {
  ideaId: string;
  userId: string;
  onUpdate?: () => void;
}

export const ActionPlanSection = ({ ideaId, userId, onUpdate }: ActionPlanSectionProps) => {
  const [tasks, setTasks] = useState<ActionPlanTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  const fetchActionPlan = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("action_plans")
        .select("*")
        .eq("idea_id", ideaId)
        .order("day_number", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error("Error fetching action plan:", error);
      toast({
        title: "Error",
        description: "Failed to load action plan",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [ideaId, toast]);

  useEffect(() => {
    fetchActionPlan();

    // Set up realtime subscription
    const channel = supabase
      .channel(`action_plans_${ideaId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "action_plans",
          filter: `idea_id=eq.${ideaId}`,
        },
        () => {
          fetchActionPlan();
          onUpdate?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ideaId, fetchActionPlan, onUpdate]);

  const handleGeneratePlan = async () => {
    setGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-action-plan", {
        body: { ideaId },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Generated ${data.taskCount} tasks for your 7-day action plan!`,
      });

      // Scroll to action plan section
      setTimeout(() => {
        const element = document.getElementById("action-plan-section");
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    } catch (error) {
      console.error("Error generating action plan:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate action plan",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleTaskStatusChange = async (taskId: string, checked: boolean) => {
    const newStatus = checked ? "done" : "todo";
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );

    try {
      const { error } = await supabase
        .from("action_plans")
        .update({ status: newStatus })
        .eq("id", taskId);

      if (error) throw error;

      // Log progress if marked as done
      if (checked) {
        await supabase.from("progress_logs").insert({
          idea_id: ideaId,
          user_id: userId,
          metric: "action_plan_task_done",
          value: null,
          note: `Day ${task.day_number}: ${task.task}`,
        });

        toast({
          title: "Task completed! 🎉",
          description: "Great progress on your action plan",
        });
      }
    } catch (error) {
      console.error("Error updating task:", error);
      // Revert optimistic update
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: task.status } : t))
      );
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  // Group tasks by day
  const tasksByDay = tasks.reduce((acc, task) => {
    if (!acc[task.day_number]) {
      acc[task.day_number] = [];
    }
    acc[task.day_number].push(task);
    return acc;
  }, {} as Record<number, ActionPlanTask[]>);

  // Calculate progress
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === "done").length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <Card id="action-plan-section" className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <CardTitle className="text-xl">7-Day Action Plan</CardTitle>
          </div>
          {tasks.length > 0 ? (
            <Button
              size="sm"
              variant="outline"
              onClick={handleGeneratePlan}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Regenerate Plan
                </>
              )}
            </Button>
          ) : (
            <Button size="sm" onClick={handleGeneratePlan} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Target className="h-4 w-4 mr-1" />
                  Generate Plan
                </>
              )}
            </Button>
          )}
        </div>
        <CardDescription>
          AI-generated personalized steps to bring your idea to life
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8">
            <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              No plan yet — click Generate 7-Day Plan to get personalized steps.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Overall Progress</span>
                <span className="font-semibold">
                  {completedTasks}/{totalTasks} tasks completed
                </span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>

            {/* Tasks by Day */}
            <Accordion type="multiple" className="w-full" defaultValue={["day-1"]}>
              {Object.entries(tasksByDay).map(([day, dayTasks]) => {
                const dayCompleted = dayTasks.every((t) => t.status === "done");
                const dayInProgress = dayTasks.some(
                  (t) => t.status === "in_progress" || t.status === "done"
                );

                return (
                  <AccordionItem key={day} value={`day-${day}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">Day {day}</span>
                          {dayCompleted && (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                              ✓ Complete
                            </Badge>
                          )}
                          {!dayCompleted && dayInProgress && (
                            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                              In Progress
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {dayTasks.filter((t) => t.status === "done").length}/{dayTasks.length}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        {dayTasks.map((task) => (
                          <div
                            key={task.id}
                            className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                          >
                            <Checkbox
                              id={task.id}
                              checked={task.status === "done"}
                              onCheckedChange={(checked) =>
                                handleTaskStatusChange(task.id, checked as boolean)
                              }
                              className="mt-1"
                            />
                            <label
                              htmlFor={task.id}
                              className={`flex-1 text-sm cursor-pointer ${
                                task.status === "done"
                                  ? "line-through text-muted-foreground"
                                  : ""
                              }`}
                            >
                              {task.task}
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
